import { createTheme, Theme } from '@mui/material/styles';
import type { PaletteKey } from './stores/themeStore';

// ─── Editorial Neutral (Taupe/Black) design token constants ─────────────────
export const EDITORIAL_NEUTRAL = {
  pureBlack: '#000000',
  refinedTaupe: '#A6948A',
  cream: '#FFFDD0',
  espressoCard: '#1A1A1A',
  warmWhite: '#F5F0E8',
  mutedTaupeDark: '#8A7A70',
  error: '#D4564E',
} as const;

// ─── Cyber-Street design token constants ────────────────────────────────────
export const CYBER_STREET = {
  deepOnyx: '#0D0D0D',
  cyberLime: '#7EC8A0',
  electricCobalt: '#5B8DEF',
  paperWhite: '#F5F5F7',
  mutedMauve: '#C4A1E0',
  error: '#FF5252',
} as const;

// ─── Modern-Minimalist design token constants ───────────────────────────────
export const MODERN_MINIMALIST = {
  softBone: '#F2F0EA',
  trueBlack: '#000000',
  sageGrey: '#8A9A5B',
  espresso: '#1C1C1C',
  burntTerracotta: '#E2725B',
  error: '#D32F2F',
} as const;

// ─── Dark Luxe design token constants ───────────────────────────────────────
export const DARK_LUXE = {
  deepCharcoal: '#0A0A0A',
  cardSurface: '#1A1A2E',
  electricViolet: '#7C3AED',
  hotPink: '#EC4899',
  warmGold: '#F59E0B',
  softWhite: '#F8FAFC',
  mutedLavender: '#A78BFA',
  error: '#F43F5E',
} as const;

// ─── Glassmorphism presets per palette ──────────────────────────────────────
export const GLASS = {
  'editorial-neutral': {
    navBar: {
      background: 'rgba(0, 0, 0, 0.85)',
      blur: 'blur(24px)',
      border: '1px solid rgba(166, 148, 138, 0.12)',
    },
    card: {
      background: 'rgba(26, 26, 26, 0.8)',
      blur: 'blur(20px)',
      border: '1px solid rgba(166, 148, 138, 0.10)',
    },
    fallback: 'rgba(26, 26, 26, 0.95)',
  },
  'cyber-street': {
    navBar: {
      background: 'rgba(13, 13, 13, 0.7)',
      blur: 'blur(20px)',
      border: '1px solid rgba(245, 245, 247, 0.08)',
    },
    card: {
      background: 'rgba(18, 18, 18, 0.6)',
      blur: 'blur(16px)',
      border: '1px solid rgba(245, 245, 247, 0.06)',
    },
    fallback: 'rgba(18, 18, 18, 0.95)',
  },
  'modern-minimalist': {
    navBar: {
      background: 'rgba(242, 240, 234, 0.75)',
      blur: 'blur(20px)',
      border: '1px solid rgba(28, 28, 28, 0.08)',
    },
    card: {
      background: 'rgba(255, 255, 255, 0.6)',
      blur: 'blur(16px)',
      border: '1px solid rgba(28, 28, 28, 0.06)',
    },
    fallback: 'rgba(255, 255, 255, 0.95)',
  },
  'dark-luxe': {
    navBar: {
      background: 'rgba(10, 10, 10, 0.75)',
      blur: 'blur(24px)',
      border: '1px solid rgba(124, 58, 237, 0.15)',
    },
    card: {
      background: 'rgba(26, 26, 46, 0.55)',
      blur: 'blur(20px)',
      border: '1px solid rgba(124, 58, 237, 0.12)',
    },
    fallback: 'rgba(26, 26, 46, 0.95)',
  },
} as const;

// ─── Font family stacks ────────────────────────────────────────────────────
export const DISPLAY_FONT = "'General Sans', 'Clash Display', 'Satoshi', -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif";
export const HEADING_FONT = DISPLAY_FONT;
export const BODY_FONT = "'Inter', -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif";
export const EDITORIAL_SERIF_FONT = "'Georgia', 'Playfair Display', 'Times New Roman', serif";
export const EDITORIAL_BODY_FONT = "'Inter', -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif";

// ─── Gradient accent system per palette ─────────────────────────────────────
export const GRADIENT_ACCENTS = {
  'editorial-neutral': {
    primary: 'linear-gradient(135deg, #A6948A, #FFFDD0)',
    secondary: 'linear-gradient(135deg, #8A7A70, #A6948A)',
    tag: {
      neonPink: '#C4A08A',
      neonPurple: '#A6948A',
      neonBlue: '#8A9A9B',
      neonGreen: '#9A9A7A',
      neonYellow: '#FFFDD0',
    },
  },
  'dark-luxe': {
    primary: 'linear-gradient(135deg, #7C3AED, #EC4899)',
    secondary: 'linear-gradient(135deg, #F59E0B, #EF4444)',
    tag: {
      neonPink: '#FF6B9D',
      neonPurple: '#B794F6',
      neonBlue: '#63B3ED',
      neonGreen: '#68D391',
      neonYellow: '#F6E05E',
    },
  },
  'cyber-street': {
    primary: 'linear-gradient(135deg, #7EC8A0, #5B8DEF)',
    secondary: 'linear-gradient(135deg, #C4A1E0, #7EC8A0)',
    tag: {
      neonPink: '#FF6B9D',
      neonPurple: '#C4A1E0',
      neonBlue: '#5B8DEF',
      neonGreen: '#7EC8A0',
      neonYellow: '#F6E05E',
    },
  },
  'modern-minimalist': {
    primary: 'linear-gradient(135deg, #8A9A5B, #E2725B)',
    secondary: 'linear-gradient(135deg, #E2725B, #F59E0B)',
    tag: {
      neonPink: '#E2725B',
      neonPurple: '#8A7A9B',
      neonBlue: '#5B7D8A',
      neonGreen: '#8A9A5B',
      neonYellow: '#D4A843',
    },
  },
} as const;

// ─── Tag color array for easy iteration ─────────────────────────────────────
export const TAG_COLORS = (palette: PaletteKey): string[] => {
  const tags = GRADIENT_ACCENTS[palette].tag;
  return [tags.neonPink, tags.neonPurple, tags.neonBlue, tags.neonGreen, tags.neonYellow];
};

// ─── Palette-specific value resolver ────────────────────────────────────────
function pick<T>(palette: PaletteKey, cyber: T, minimalist: T, luxe: T, editorial?: T): T {
  if (palette === 'editorial-neutral') return editorial !== undefined ? editorial : luxe;
  if (palette === 'cyber-street') return cyber;
  if (palette === 'modern-minimalist') return minimalist;
  return luxe;
}

// ─── App theme factory ─────────────────────────────────────────────────────
export function createAppTheme(palette: PaletteKey): Theme {
  const isLuxe = palette === 'dark-luxe';
  const isEditorial = palette === 'editorial-neutral';
  const isDarkMode = palette === 'cyber-street' || isLuxe || isEditorial;
  const glass = GLASS[palette];

  // Editorial Neutral uses serif for headings, sans-serif for body
  const headingFont = isEditorial ? EDITORIAL_SERIF_FONT : HEADING_FONT;
  const bodyFont = isEditorial ? EDITORIAL_BODY_FONT : BODY_FONT;

  return createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: pick(palette, CYBER_STREET.cyberLime, MODERN_MINIMALIST.trueBlack, DARK_LUXE.electricViolet, EDITORIAL_NEUTRAL.refinedTaupe),
        contrastText: pick(palette, CYBER_STREET.deepOnyx, MODERN_MINIMALIST.softBone, DARK_LUXE.softWhite, EDITORIAL_NEUTRAL.pureBlack),
      },
      secondary: {
        main: pick(palette, CYBER_STREET.electricCobalt, MODERN_MINIMALIST.sageGrey, DARK_LUXE.hotPink, EDITORIAL_NEUTRAL.cream),
        contrastText: pick(palette, CYBER_STREET.paperWhite, MODERN_MINIMALIST.softBone, DARK_LUXE.softWhite, EDITORIAL_NEUTRAL.pureBlack),
      },
      background: {
        default: pick(palette, CYBER_STREET.deepOnyx, MODERN_MINIMALIST.softBone, DARK_LUXE.deepCharcoal, EDITORIAL_NEUTRAL.pureBlack),
        paper: pick(palette, '#1A1A1A', '#FFFFFF', DARK_LUXE.cardSurface, EDITORIAL_NEUTRAL.espressoCard),
      },
      text: {
        primary: pick(palette, CYBER_STREET.paperWhite, MODERN_MINIMALIST.espresso, DARK_LUXE.softWhite, EDITORIAL_NEUTRAL.cream),
        secondary: pick(palette,
          'rgba(245, 245, 247, 0.6)',
          'rgba(28, 28, 28, 0.6)',
          'rgba(248, 250, 252, 0.55)',
          'rgba(166, 148, 138, 0.8)',
        ),
      },
      ...(isLuxe ? { warning: { main: DARK_LUXE.warmGold } } : {}),
      ...(isEditorial ? { warning: { main: EDITORIAL_NEUTRAL.refinedTaupe } } : {}),
      error: { main: pick(palette, CYBER_STREET.error, MODERN_MINIMALIST.error, DARK_LUXE.error, EDITORIAL_NEUTRAL.error) },
    },
    typography: {
      fontFamily: bodyFont,
      h1: {
        fontFamily: headingFont,
        fontWeight: isEditorial ? 400 : 800,
        fontSize: '3rem',
        ...(isEditorial ? { letterSpacing: '0.04em' } : {}),
      },
      h2: {
        fontFamily: headingFont,
        fontWeight: isEditorial ? 400 : 700,
        fontSize: '2.25rem',
        ...(isEditorial ? { letterSpacing: '0.03em' } : {}),
      },
      h3: {
        fontFamily: headingFont,
        fontWeight: isEditorial ? 400 : 700,
        fontSize: '1.75rem',
        ...(isEditorial ? { letterSpacing: '0.02em' } : {}),
      },
      h4: {
        fontFamily: headingFont,
        fontWeight: isEditorial ? 500 : 600,
        fontSize: '1.5rem',
      },
      h5: {
        fontFamily: headingFont,
        fontWeight: isEditorial ? 500 : 600,
        fontSize: '1.25rem',
      },
      h6: {
        fontFamily: headingFont,
        fontWeight: isEditorial ? 500 : 600,
        fontSize: '1rem',
      },
      body1: {
        fontFamily: bodyFont,
      },
      body2: {
        fontFamily: BODY_FONT,
      },
      caption: {
        fontFamily: BODY_FONT,
      },
      overline: {
        fontFamily: BODY_FONT,
      },
      button: {
        fontFamily: BODY_FONT,
        fontWeight: 700,
        textTransform: 'none' as const,
        letterSpacing: '0.02em',
      },
    },
    shape: {
      borderRadius: 12, // default for Button/input
    },
    components: {
      // ── Global baseline ──────────────────────────────────────────────
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: pick(palette, CYBER_STREET.deepOnyx, MODERN_MINIMALIST.softBone, DARK_LUXE.deepCharcoal, EDITORIAL_NEUTRAL.pureBlack),
            color: pick(palette, CYBER_STREET.paperWhite, MODERN_MINIMALIST.espresso, DARK_LUXE.softWhite, EDITORIAL_NEUTRAL.cream),
            transition: 'background-color 200ms ease, color 200ms ease',
            // prefers-reduced-motion: disable all transitions/animations globally
            // The '&' targets body itself, '*' targets all descendants
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none !important',
              animation: 'none !important',
              '&, *': {
                animationDuration: '0ms !important',
                animationDelay: '0ms !important',
                transitionDuration: '0ms !important',
                transitionDelay: '0ms !important',
              },
              // Disable all transform-based hover effects
              '*, *::before, *::after': {
                animation: 'none !important',
                transition: 'none !important',
              },
            },
          },
          '#root': {
            minHeight: '100vh',
            background: 'transparent',
          },
        },
      },

      // ── Card: glassmorphism + light grey shadow outline + 16px radius ─
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundColor: glass.card.background,
            backdropFilter: glass.card.blur,
            WebkitBackdropFilter: glass.card.blur,
            border: pick(palette,
              '1px solid rgba(255, 255, 255, 0.25)',
              '1px solid rgba(0, 0, 0, 0.10)',
              `1px solid rgba(124, 58, 237, 0.18)`,
              '1px solid rgba(166, 148, 138, 0.15)',
            ),
            boxShadow: pick(palette,
              '0 0 0 1px rgba(255, 255, 255, 0.08), 0 4px 24px rgba(0, 0, 0, 0.4)',
              '0 1px 4px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.08)',
              '0 0 0 1px rgba(124, 58, 237, 0.10), 0 8px 32px rgba(0, 0, 0, 0.5)',
              '0 4px 24px rgba(0, 0, 0, 0.6)',
            ),
            '@supports not (backdrop-filter: blur(1px))': {
              backgroundColor: glass.fallback,
            },
          },
        },
      },

      // ── Paper: glassmorphism + light grey shadow outline + 16px radius
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundColor: glass.card.background,
            backdropFilter: glass.card.blur,
            WebkitBackdropFilter: glass.card.blur,
            border: pick(palette,
              '1px solid rgba(255, 255, 255, 0.25)',
              '1px solid rgba(0, 0, 0, 0.10)',
              `1px solid rgba(124, 58, 237, 0.18)`,
              '1px solid rgba(166, 148, 138, 0.15)',
            ),
            boxShadow: pick(palette,
              '0 0 0 1px rgba(255, 255, 255, 0.08), 0 4px 24px rgba(0, 0, 0, 0.4)',
              '0 1px 4px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.08)',
              '0 0 0 1px rgba(124, 58, 237, 0.10), 0 8px 32px rgba(0, 0, 0, 0.5)',
              '0 4px 24px rgba(0, 0, 0, 0.6)',
            ),
            '@supports not (backdrop-filter: blur(1px))': {
              backgroundColor: glass.fallback,
            },
          },
        },
      },

      // ── Button: per-palette contained styles + 12px radius ───────────
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            textTransform: 'none' as const,
          },
          containedPrimary: {
            backgroundColor: pick(palette, CYBER_STREET.cyberLime, MODERN_MINIMALIST.trueBlack, DARK_LUXE.electricViolet, EDITORIAL_NEUTRAL.refinedTaupe),
            color: pick(palette, CYBER_STREET.deepOnyx, MODERN_MINIMALIST.softBone, DARK_LUXE.softWhite, EDITORIAL_NEUTRAL.pureBlack),
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: pick(palette, CYBER_STREET.cyberLime, MODERN_MINIMALIST.trueBlack, DARK_LUXE.electricViolet, EDITORIAL_NEUTRAL.refinedTaupe),
              boxShadow: pick(palette,
                '0 0 20px rgba(126, 200, 160, 0.3)',
                '0 4px 16px rgba(0, 0, 0, 0.15)',
                '0 0 24px rgba(124, 58, 237, 0.4)',
                '0 4px 16px rgba(166, 148, 138, 0.3)',
              ),
              transition: 'box-shadow 200ms ease',
            },
          },
          containedSecondary: pick(palette,
            {
              backgroundColor: 'transparent',
              border: `1px solid ${CYBER_STREET.paperWhite}`,
              color: CYBER_STREET.paperWhite,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'rgba(245, 245, 247, 0.08)',
                boxShadow: 'none',
              },
            },
            {
              backgroundColor: 'transparent',
              border: `1px solid ${MODERN_MINIMALIST.espresso}`,
              color: MODERN_MINIMALIST.espresso,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'rgba(28, 28, 28, 0.06)',
                boxShadow: 'none',
              },
            },
            {
              backgroundColor: 'transparent',
              border: `1px solid ${DARK_LUXE.hotPink}`,
              color: DARK_LUXE.hotPink,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'rgba(236, 72, 153, 0.10)',
                boxShadow: 'none',
              },
            },
            {
              backgroundColor: 'transparent',
              border: `1px solid ${EDITORIAL_NEUTRAL.cream}`,
              color: EDITORIAL_NEUTRAL.cream,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'rgba(255, 253, 208, 0.08)',
                boxShadow: 'none',
              },
            },
          ),
        },
      },

      // ── Input: 12px radius ───────────────────────────────────────────
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },

      // ── AppBar: glassmorphism nav bar ────────────────────────────────
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: glass.navBar.background,
            backdropFilter: glass.navBar.blur,
            WebkitBackdropFilter: glass.navBar.blur,
            borderBottom: glass.navBar.border,
            boxShadow: pick(palette,
              'none',
              '0 1px 3px rgba(0, 0, 0, 0.06)',
              'none',
              'none',
            ),
            color: pick(palette, CYBER_STREET.paperWhite, MODERN_MINIMALIST.espresso, DARK_LUXE.softWhite, EDITORIAL_NEUTRAL.cream),
          },
        },
      },

      // ── IconButton: ensure visibility in both themes ─────────────────
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: 'inherit',
          },
        },
      },

      // ── Container ────────────────────────────────────────────────────
      MuiContainer: {
        styleOverrides: {
          root: {
            position: 'relative' as const,
            zIndex: 1,
          },
        },
      },
    },
  });
}

// ─── Admin theme (preserved original theme for admin page isolation) ────────
export function createAdminTheme(): Theme {
  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#2e7d32',
        light: '#60ad5e',
        dark: '#005005',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#ff6f00',
        light: '#ffa040',
        dark: '#c43e00',
        contrastText: '#000000',
      },
      background: {
        default: '#3a3a3a',
        paper: 'rgba(60, 60, 60, 0.95)',
      },
      error: { main: '#f44336' },
      warning: { main: '#ffa726' },
      info: { main: '#29b6f6' },
      success: { main: '#66bb6a' },
      text: {
        primary: 'rgba(255, 255, 255, 0.87)',
        secondary: 'rgba(255, 255, 255, 0.6)',
      },
    },
    typography: {
      fontFamily: [
        'Inter',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
      h1: { fontWeight: 700, fontSize: '2.5rem' },
      h2: { fontWeight: 600, fontSize: '2rem' },
      h3: { fontWeight: 600, fontSize: '1.75rem' },
      h4: { fontWeight: 600, fontSize: '1.5rem' },
      h5: { fontWeight: 600, fontSize: '1.25rem' },
      h6: { fontWeight: 600, fontSize: '1rem' },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: `
              linear-gradient(
                rgba(26, 26, 26, 0.6),
                rgba(26, 26, 26, 0.6)
              ),
              url('${process.env.PUBLIC_URL}/charcoal.jpg')
            `,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            backgroundColor: '#3a3a3a',
            minHeight: '100vh',
            '&::before': {
              content: '""',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: `
                repeating-linear-gradient(
                  15deg,
                  transparent,
                  transparent 1px,
                  rgba(255, 255, 255, 0.02) 1px,
                  rgba(255, 255, 255, 0.02) 3px
                ),
                repeating-linear-gradient(
                  75deg,
                  transparent,
                  transparent 2px,
                  rgba(255, 255, 255, 0.015) 2px,
                  rgba(255, 255, 255, 0.015) 5px
                ),
                repeating-linear-gradient(
                  -30deg,
                  transparent,
                  transparent 1px,
                  rgba(0, 0, 0, 0.03) 1px,
                  rgba(0, 0, 0, 0.03) 4px
                )
              `,
              pointerEvents: 'none',
              zIndex: -1,
            },
          },
          '#root': {
            minHeight: '100vh',
            background: 'transparent',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 8, padding: '8px 16px' },
          contained: {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            '&:hover': { boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)' },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: 'rgba(70, 70, 70, 0.92)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            boxShadow:
              '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
            '&:hover': {
              boxShadow:
                '0 12px 40px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.25)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: 'rgba(70, 70, 70, 0.92)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            boxShadow:
              '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(45, 45, 45, 0.95)',
            backdropFilter: 'blur(15px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: { position: 'relative' as const, zIndex: 1 },
        },
      },
    },
  });
}

// Default export: admin theme for backward compatibility with existing imports
const theme = createAdminTheme();
export default theme;
